/**
 * Per-source FOV range clamps (Q.3.3).
 *
 * AI FOV inference (and some upstream registries) hand us a `range_m` that
 * models open sight-line distance — it does NOT model occlusion or mount
 * elevation. So an intersection traffic cam pointed down a street gets a
 * cone reaching 800–3000 m even though it realistically sees 200–400 m
 * before buildings occlude the scene; a low-mounted aerodrome cam pointed
 * down a runway gets a 3–4 km cone reaching over the adjacent city.
 *
 * This file is the SINGLE SOURCE OF TRUTH for per-source range caps. Two
 * consumers apply it:
 *   1. `apps/ingestion/src/scripts/q3-apply-source-range-clamps.ts` — one-shot
 *      backfill over existing DB rows (idempotent).
 *   2. `apps/ai/src/tsr_ai/fov_cli.py` — clamps every future AI inference
 *      write. NOTE: the Python write path mirrors these constants in
 *      `apps/ai/src/tsr_ai/source_range_overrides.py` (it cannot import TS);
 *      keep the two in sync. The mirror's docstring points back here.
 *
 * To add a new per-source rule, edit ONLY this file (and the Python mirror)
 * — both consumers read `clampRangeForSource()`.
 */

export interface SourceRangeOverride {
  maxRangeM: number;
  rationale: string;
}

/**
 * Keyed by `cameras.source`. Dedicated traffic sources (municipal +
 * provincial 511) are all pointed at intersection level — real viewshed
 * 200–400 m — so they share the 300 m urban cap.
 */
export const SOURCE_RANGE_OVERRIDES: Record<string, SourceRangeOverride> = {
  // Urban municipal traffic — pointed at intersection level, real viewshed 200–400 m
  'toronto-traffic': { maxRangeM: 300, rationale: 'Urban traffic intersection cam' },
  'ottawa-traffic': { maxRangeM: 300, rationale: 'Urban traffic intersection cam' },
  'durham-region': { maxRangeM: 300, rationale: 'Urban traffic intersection cam' },
  'york-region': { maxRangeM: 300, rationale: 'Urban traffic intersection cam' },
  'calgary-traffic': { maxRangeM: 300, rationale: 'Urban traffic intersection cam' },
  // Provincial traffic — same intersection/roadway framing
  'on511cams': { maxRangeM: 300, rationale: 'Provincial traffic cam' },
  'qc511-mtq': { maxRangeM: 300, rationale: 'Provincial traffic cam' },
  // DAWC aerodrome cams (NAV CANADA metcam) currently arrive under
  // source='tsr-catalog' (sheet='Airports'), so they are clamped via
  // CATALOG_SHEET_RANGE_OVERRIDES below, NOT this map. This key is kept as a
  // defensive forward-rule in case DAWC is ever promoted to its own source.
  dawc: { maxRangeM: 1500, rationale: 'Aerodrome cam pointing down runway' },
};

/**
 * Keyed by the XLSX sheet name stored in `metadata->>'sheet'` for
 * `source='tsr-catalog'` rows. This is where the real DAWC/airport fix and
 * the bulk of the traffic-cone fix land — the dedicated traffic sources
 * above are mostly heading-unknown discs, while the catalog Traffic/Airports
 * sheets carry the inferred + upstream directional cones that overshoot.
 */
export const CATALOG_SHEET_RANGE_OVERRIDES: Record<string, SourceRangeOverride> = {
  Traffic: { maxRangeM: 300, rationale: 'Catalog Traffic sheet — urban intersection cams' },
  Airports: { maxRangeM: 1500, rationale: 'Catalog Airports sheet — DAWC + ops cams' },
};

/**
 * Hard ceiling for a HEADING-UNKNOWN coverage disc (QA-7 backstop).
 *
 * A heading-unknown camera renders as an omnidirectional disc of radius
 * `range_m` — the ambient "covered roughly here" register (we don't know where
 * it points). Unlike a directional cone, a disc's painted area is the full
 * circle, so a large `range_m` paints a city-swallowing footprint: the QA-7
 * "giant blue disc over most of Toronto" was a downtown panorama row with a
 * 3000 m sheet-default range that escaped the per-source cone caps above.
 *
 * Because an unknown disc carries no aim information, it never needs a multi-km
 * radius — capping it at 1500 m (the same scale as a known aerodrome viewshed)
 * guarantees no ambient disc can ever swallow a city, while leaving directional
 * cones (source-capped) and deliberate wide-area PTZ fire-tower 360° discs
 * (which intentionally render at their authoritative range) untouched. Applied
 * BOTH at render time (apps/web MapView unknown-disc branch) and as a one-shot
 * data clamp (qa7-unknown-disc-range-clamp.ts) so stored data and the painted
 * footprint agree.
 */
export const MAX_UNKNOWN_DISC_RANGE_M = 1500;

/** Clamp a heading-unknown disc's range to {@link MAX_UNKNOWN_DISC_RANGE_M}. */
export function clampUnknownDiscRange(rangeM: number): number {
  return Math.min(rangeM, MAX_UNKNOWN_DISC_RANGE_M);
}

/**
 * Clamp an inferred range to the per-source cap. Source-keyed overrides win;
 * for tsr-catalog rows we fall back to the sheet-keyed override. Returns the
 * input unchanged when no rule applies — so this is always safe to call.
 */
export function clampRangeForSource(
  inferredRangeM: number,
  source: string,
  sheet: string | null,
): number {
  const sourceOverride = SOURCE_RANGE_OVERRIDES[source];
  if (sourceOverride) return Math.min(inferredRangeM, sourceOverride.maxRangeM);
  if (source === 'tsr-catalog' && sheet) {
    const sheetOverride = CATALOG_SHEET_RANGE_OVERRIDES[sheet];
    if (sheetOverride) return Math.min(inferredRangeM, sheetOverride.maxRangeM);
  }
  return inferredRangeM;
}
